import React, { useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useAuth } from '../contexts/AppContext';

const POLL_INTERVAL_MS = 60_000; // 60s

/**
 * NotificationBell — sidebar/header bell with unread badge.
 * Polls /notifications/unread-count on an interval and on a custom
 * `audivue:notifications:refresh` window event so other pages can
 * trigger an immediate refresh after they mark items read.
 *
 * `variant`: "sidebar" (desktop pill) | "compact" (mobile / inline icon)
 */
export const NotificationBell = ({ variant = 'sidebar' }) => {
  const { user, axiosInstance } = useAuth();
  const [count, setCount] = useState(0);

  const fetchCount = useCallback(async () => {
    if (!user) {
      setCount(0);
      return;
    }
    try {
      const res = await axiosInstance.get('/notifications/unread-count');
      setCount(Number(res?.data?.count) || 0);
    } catch (e) {
      // Silent — bell badge is best-effort. 401s will resolve next render.
    }
  }, [user, axiosInstance]);

  useEffect(() => {
    fetchCount();
    if (!user) return undefined;
    const id = setInterval(fetchCount, POLL_INTERVAL_MS);
    const onRefresh = () => fetchCount();
    window.addEventListener('audivue:notifications:refresh', onRefresh);
    return () => {
      clearInterval(id);
      window.removeEventListener('audivue:notifications:refresh', onRefresh);
    };
  }, [user, fetchCount]);

  if (!user) return null;

  const displayCount = count > 99 ? '99+' : String(count);
  const hasUnread = count > 0;

  if (variant === 'compact') {
    return (
      <NavLink
        to="/notifications"
        data-testid="notification-bell-compact"
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-zinc-800/60 transition-colors"
        aria-label={`Notifications${hasUnread ? ` (${displayCount} unread)` : ''}`}
      >
        <Bell className="w-5 h-5 text-white" />
        {hasUnread && (
          <span
            data-testid="notification-bell-badge"
            className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-[10px] font-bold text-white flex items-center justify-center ring-2 ring-black"
          >
            {displayCount}
          </span>
        )}
      </NavLink>
    );
  }

  // Sidebar variant — pill that matches Sidebar nav row styling.
  return (
    <NavLink
      to="/notifications"
      data-testid="notification-bell-sidebar"
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
          isActive ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
        }`
      }
    >
      <span className="relative">
        <Bell className="w-5 h-5" />
        {hasUnread && (
          <span
            data-testid="notification-bell-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-[10px] font-bold text-white flex items-center justify-center"
          >
            {displayCount}
          </span>
        )}
      </span>
      <span className="font-medium">Notifications</span>
    </NavLink>
  );
};

export default NotificationBell;
